import http from '../../shared/http.js';
import { withFlash } from '../../shared/flash.js';
import { confirmAction, hideLoading, notifyError, showLoading } from '../../shared/notify.js';

// The candidates proposed for a job, managed from the job's own page. There is
// no standalone list, so everything here works against one section of the detail
// page and reloads it when something changes - the same shape as the customer's
// ansprechpersonInline.js next door.
//
// The Termine inside each row are the neighbouring module's job (terminInline.js),
// so this file owns the proposal rows and that one owns what folds out beneath
// them. Two modules rather than one because they share nothing but the table
// they are rendered into.

// The statuses that need a reason before they can be applied. Mirrors
// ABLEHNUNG_STATI in utils/vorschlagKatalog.js - the server refuses these
// without one regardless, and this only means the dialog asks first instead of
// the request coming back a 400.
const ABLEHNUNG_STATI = ['kunde_abgelehnt', 'tma_abgelehnt', 'storniert_intern'];

// Reloads the page showing a flash. The table is rendered server-side, so a
// round trip is what puts a new row on screen - and it also refreshes the
// Verlauf column, which every action here writes an entry to.
function reloadWith(key) {
  // Keeps the query string: the "Abgesagte anzeigen" checkbox is a plain GET
  // field, and reloading onto the bare pathname would close it again on every
  // status change (C01).
  window.location.href = withFlash(window.location.pathname + window.location.search, key);
}

// Both modules report a failed request the same way, and both have to put the
// control they disabled back the way they found it.
function fail(err, button) {
  hideLoading();
  if (button) button.disabled = false;
  notifyError(
    err.response?.data?.message || 'Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.'
  );
}

export function initKandidatenvorschlagInline() {
  const liste = document.getElementById('kandidatenvorschlag-liste');
  if (!liste) return;

  const { jobId } = liste.dataset;

  const neuModal = document.getElementById('kandidatenvorschlag-modal');
  const neuForm = document.getElementById('kandidatenvorschlag-form');
  const ablehnungModal = document.getElementById('vorschlag-ablehnung-modal');
  const ablehnungForm = document.getElementById('vorschlag-ablehnung-form');

  // Which proposal the Ablehnung dialog is about, and which status it is
  // applying. Held here rather than in a hidden field because it never round
  // trips through the form - the dialog is opened by a pick that already knows
  // both, exactly the way the job's Verlust dialog works.
  let offeneAblehnung = null;

  // Sends a status change. The one place the transition endpoint is called, so
  // the picker, the dialog and any future caller agree on the payload.
  async function sendeStatus(id, status, ablehnungsgrund = null) {
    showLoading('Wird gespeichert …');
    try {
      await http.patch(`/kandidatenvorschlaege/${id}/status`, {
        status,
        ...(ablehnungsgrund ? { ablehnungsgrund } : {})
      });
      reloadWith('vorschlag-aktualisiert');
    } catch (err) {
      fail(err);
    }
  }

  document.getElementById('kandidatenvorschlag-neu')?.addEventListener('click', () => {
    neuForm.reset();
    neuModal.showModal();
  });

  neuForm
    ?.querySelector('[data-action="abbrechen"]')
    ?.addEventListener('click', () => neuModal.close());
  ablehnungForm
    ?.querySelector('[data-action="abbrechen"]')
    ?.addEventListener('click', () => ablehnungModal.close());

  // One delegated listener for the whole table, so rows cost nothing to add.
  // Clicks only - the status picker's own change event is bound below, because a
  // <select> does not bubble a click when it is changed by keyboard.
  liste.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-action]');
    if (!button) return;

    // Opens the picker in place of the badge. Focused straight away so the list
    // drops open on a click and the keyboard lands somewhere useful.
    if (button.dataset.action === 'status-oeffnen') {
      const feld = button.closest('.vorschlag-status-feld');
      const select = feld?.querySelector('[data-role="status-select"]');
      if (!select) return;

      button.classList.add('hidden');
      select.hidden = false;
      select.classList.remove('hidden');
      select.focus();
      return;
    }

    if (button.dataset.action !== 'vorschlag-loeschen') return;

    const feld = button.closest('tr')?.querySelector('.vorschlag-status-feld');
    const id = feld?.dataset.id;
    const { name } = button.dataset;

    const confirmed = await confirmAction({
      title: 'Vorschlag entfernen',
      message: `Möchten Sie den Vorschlag für ${name} endgültig entfernen? Das kann nicht rückgängig gemacht werden.`,
      okText: 'Entfernen'
    });
    if (!confirmed) return;

    button.disabled = true;
    showLoading('Vorschlag wird entfernt …');

    try {
      await http.delete(`/kandidatenvorschlaege/${id}`);
      reloadWith('vorschlag-entfernt');
    } catch (err) {
      fail(err, button);
    }
  });

  // The status picks. A change rather than a click, so the keyboard works.
  liste.addEventListener('change', (event) => {
    const select = event.target.closest('[data-role="status-select"]');
    if (!select) return;

    const feld = select.closest('.vorschlag-status-feld');
    const { id, status: statusAlt } = feld.dataset;
    const statusNeu = select.value;

    if (statusNeu === statusAlt) return;

    // The three ending states ask for their reason first: the schema refuses
    // them without one, so applying the pick directly could only come back a
    // 400. Everything else goes straight through.
    if (ABLEHNUNG_STATI.includes(statusNeu)) {
      offeneAblehnung = { id, status: statusNeu };
      ablehnungForm.reset();
      ablehnungModal.showModal();
      return;
    }

    sendeStatus(id, statusNeu);
  });

  // Escape or a click away puts the badge back, so a picker opened by accident
  // does not stay open over the row. Focusout rather than blur, because blur
  // does not bubble and this listener is delegated.
  liste.addEventListener('focusout', (event) => {
    const select = event.target.closest('[data-role="status-select"]');
    if (!select) return;

    const feld = select.closest('.vorschlag-status-feld');
    select.hidden = true;
    select.classList.add('hidden');
    feld?.querySelector('[data-action="status-oeffnen"]')?.classList.remove('hidden');
  });

  neuForm?.addEventListener('submit', async (event) => {
    event.preventDefault();

    const data = Object.fromEntries(new FormData(neuForm).entries());
    const submitButton = neuForm.querySelector('button[type="submit"]');

    if (submitButton) submitButton.disabled = true;
    showLoading('Wird gespeichert …');

    try {
      await http.post(`/jobs/${jobId}/kandidatenvorschlaege`, data);
      reloadWith('vorschlag-erstellt');
    } catch (err) {
      fail(err, submitButton);
    }
  });

  ablehnungForm?.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (!offeneAblehnung) return;

    const grund = ablehnungForm.querySelector('#vorschlag-ablehnungsgrund').value.trim();
    if (!grund) return;

    const { id, status } = offeneAblehnung;
    offeneAblehnung = null;
    ablehnungModal.close();

    await sendeStatus(id, status, grund);
  });
}
